/**
 * Período de análisis y normalización de marcas de tiempo.
 *
 * @ported-from GuardIA1224/guardia-platform-sites@776c789 src/analysis/analysis-period.ts
 * Portado sin cambios de comportamiento. Solo formato y comentarios.
 *
 * Todo el sistema opera en America/Bogota (UTC-05:00, sin horario de verano).
 * Un timestamp sin offset se interpreta en esa zona; uno con offset distinto se
 * convierte a ella. La zona no se configura por usuario a propósito: cambiarla
 * cambiaría los resultados históricos y rompería la reproducibilidad.
 */

export const ANALYSIS_TIMEZONE = "America/Bogota" as const;
export const BOGOTA_OFFSET = "-05:00" as const;

export type AnalysisPeriod = {
  fromIso: string;
  toIso: string;
  timezone: typeof ANALYSIS_TIMEZONE;
  fromEpochMs: number;
  toEpochMs: number;
  durationSeconds: number;
};

const ISO_WITH_OFFSET =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(-05:00)$/u;
const PROVIDER_DMY = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/u;
const PROVIDER_YMD = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/u;
const ISO_ANY_OFFSET =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/u;

/**
 * Valida que la fecha exista de verdad. `Date.parse` acepta "2026-02-31" y lo
 * corre al 3 de marzo; aquí eso sería un período distinto del pedido.
 */
function calendarIsValid(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) return false;
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function validateParts(match: RegExpMatchArray): boolean {
  return calendarIsValid(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6]),
  );
}

/** Acepta únicamente ISO con offset -05:00. Cualquier otra cosa devuelve null. */
export function parseBogotaIso(value: string): number | null {
  const match = value.match(ISO_WITH_OFFSET);
  if (!match || !validateParts(match)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Acepta ISO con cualquier offset o Z. Usado para timestamps ya normalizados. */
export function parseStrictIsoTimestamp(value: string | null): number | null {
  if (!value) return null;
  const match = value.match(ISO_ANY_OFFSET);
  if (!match || !validateParts(match)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Compone un ISO de Bogotá desde los valores de un <input type="date"> + hora. */
export function dateInputToBogotaIso(date: string, time: string): string | null {
  const candidate = `${date}T${time}:00${BOGOTA_OFFSET}`;
  return parseBogotaIso(candidate) === null ? null : candidate;
}

/**
 * Fecha de hoy en Bogotá, en formato de <input type="date">.
 * Vive aquí por cercanía temática, pero recibe el instante: el core no tiene
 * reloj propio (constitución, Art. 1).
 */
export function bogotaDateInputValue(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ANALYSIS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = new Map(parts.map((part) => [part.type, part.value]));
  return `${value.get("year")}-${value.get("month")}-${value.get("day")}`;
}

export function parseAnalysisPeriod(value: {
  fromIso: unknown;
  toIso: unknown;
  timezone: unknown;
}): AnalysisPeriod | null {
  if (typeof value.fromIso !== "string") return null;
  if (typeof value.toIso !== "string") return null;
  if (value.timezone !== ANALYSIS_TIMEZONE) return null;

  const fromEpochMs = parseBogotaIso(value.fromIso);
  const toEpochMs = parseBogotaIso(value.toIso);
  if (fromEpochMs === null || toEpochMs === null || fromEpochMs > toEpochMs) return null;

  return {
    fromIso: value.fromIso,
    toIso: value.toIso,
    timezone: ANALYSIS_TIMEZONE,
    fromEpochMs,
    toEpochMs,
    durationSeconds: (toEpochMs - fromEpochMs) / 1000,
  };
}

/** WOX pide fecha y hora por separado, sin offset. */
export function providerParametersFromPeriod(period: AnalysisPeriod) {
  return {
    fromDate: period.fromIso.slice(0, 10),
    fromTime: period.fromIso.slice(11, 16),
    toDate: period.toIso.slice(0, 10),
    toTime: period.toIso.slice(11, 16),
  };
}

function providerPartsToIso(match: RegExpMatchArray, dmy: boolean): string | null {
  const year = dmy ? match[3] : match[1];
  const month = match[2];
  const day = dmy ? match[1] : match[3];
  const fraction = match[7] ? `.${match[7]}` : "";
  const candidate = `${year}-${month}-${day}T${match[4]}:${match[5]}:${match[6]}${fraction}${BOGOTA_OFFSET}`;
  return parseBogotaIso(candidate) === null ? null : candidate;
}

/**
 * WOX devuelve la hora en varios formatos según el endpoint y el idioma.
 * Los que vienen sin offset se interpretan como hora de Bogotá; los que traen
 * offset se convierten a Bogotá. Lo que no calce en ningún patrón devuelve
 * null y el registro se descarta — no se adivina.
 */
export function normalizeGpswoxTimestamp(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();

  const dmy = text.match(PROVIDER_DMY);
  if (dmy) return providerPartsToIso(dmy, true);

  const ymd = text.match(PROVIDER_YMD);
  if (ymd) return providerPartsToIso(ymd, false);

  const parsed = parseStrictIsoTimestamp(text);
  if (parsed === null) return null;
  return new Date(parsed - 5 * 60 * 60 * 1000).toISOString().replace("Z", BOGOTA_OFFSET);
}
